import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND_COLORS } from "@/lib/menuData";
import Footer from "@/components/Footer";

export default function TermsOfService() {
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: BRAND_COLORS.sandWhite }}>
      {/* Header */}
      <header className="py-4 px-6" style={{ backgroundColor: BRAND_COLORS.deepTeal }}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-white hover:bg-white/10">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Home
            </Button>
          </Link>
          <div className="flex items-center gap-2 text-white">
            <FileText className="h-5 w-5" />
            <span className="font-semibold text-sm">Terms of Service</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 py-8 px-6">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm p-8">
          <h1 className="text-2xl font-bold mb-6" style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>
            Terms of Service
          </h1>
          <p className="text-sm text-gray-500 mb-6">Last Updated: February 2026</p>

          <div className="prose prose-sm max-w-none" style={{ color: BRAND_COLORS.darkSlate }}>
            <h2 className="text-lg font-semibold mt-6 mb-3">1. Agreement to Terms</h2>
            <p className="mb-4">
              By accessing or using the Dauphin Island Food Mart website and online ordering platform, 
              you agree to be bound by these Terms of Service. If you do not agree to these terms, 
              please do not use our services.
            </p>

            <h2 className="text-lg font-semibold mt-6 mb-3">2. Online Ordering</h2>
            <h3 className="text-base font-medium mt-4 mb-2">Order Placement</h3>
            <p className="mb-4">
              When you place an order through our website, you are making an offer to purchase the items 
              in your order. We reserve the right to accept or decline your order for any reason, including 
              but not limited to item availability, errors in pricing, or suspected fraudulent activity.
            </p>

            <h3 className="text-base font-medium mt-4 mb-2">Order Accuracy</h3>
            <p className="mb-4">
              You are responsible for ensuring that your order is accurate before submitting. 
              Please review your order carefully, including customizations and special instructions.
            </p>

            <h3 className="text-base font-medium mt-4 mb-2">Pickup Orders</h3>
            <p className="mb-4">
              All orders placed through our website are for pickup only. Orders should be picked up 
              at our location within a reasonable time after they are ready. Orders not picked up 
              within 1 hour of completion may be discarded.
            </p>

            <h2 className="text-lg font-semibold mt-6 mb-3">3. Pricing and Payment</h2>
            <p className="mb-4">
              All prices displayed on our website are in US Dollars. We make every effort to ensure 
              pricing accuracy, but errors may occur. If we discover a pricing error, we will contact 
              you to confirm whether you wish to proceed with the corrected price.
            </p>
            <p className="mb-4">
              Payment is due at the time of pickup. We accept cash and major credit/debit cards.
            </p>

            <h2 className="text-lg font-semibold mt-6 mb-3">4. Cancellations and Refunds</h2>
            <p className="mb-4">
              <strong>Order Cancellation:</strong> You may request to cancel your order by contacting 
              us immediately at (251) 861-2320. If preparation has not begun, we will cancel your order 
              at no charge.
            </p>
            <p className="mb-4">
              <strong>Refunds:</strong> If you are unsatisfied with your order, please notify us immediately 
              upon pickup. We will work with you to resolve the issue, which may include remaking items 
              or providing a refund at our discretion.
            </p>
            <p className="mb-4">
              <strong>Quality Issues:</strong> For quality concerns after leaving our premises, please 
              contact us within 2 hours of pickup. We cannot guarantee refunds for issues reported after 
              this timeframe.
            </p>

            <h2 className="text-lg font-semibold mt-6 mb-3">5. Menu and Availability</h2>
            <p className="mb-4">
              Menu items and prices are subject to change without notice. Certain items may be temporarily 
              unavailable due to inventory constraints. We will notify you if an item in your order is 
              unavailable and offer alternatives when possible.
            </p>

            <h2 className="text-lg font-semibold mt-6 mb-3">6. User Accounts</h2>
            <p className="mb-4">
              You may use our services as a guest or create an account using Google Sign-In. 
              If you create an account, you are responsible for maintaining the confidentiality 
              of your account and for all activities that occur under your account.
            </p>

            <h2 className="text-lg font-semibold mt-6 mb-3">7. Prohibited Conduct</h2>
            <p className="mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Use our services for any unlawful purpose</li>
              <li>Submit false or misleading information</li>
              <li>Interfere with or disrupt our website or services</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Place fraudulent orders</li>
            </ul>

            <h2 className="text-lg font-semibold mt-6 mb-3">8. Intellectual Property</h2>
            <p className="mb-4">
              All content on our website, including text, graphics, logos, and images, is the property 
              of Dauphin Island Food Mart or its licensors and is protected by copyright and trademark laws.
            </p>

            <h2 className="text-lg font-semibold mt-6 mb-3">9. Disclaimer of Warranties</h2>
            <p className="mb-4">
              Our website and services are provided "as is" without warranties of any kind. We do not 
              warrant that our services will be uninterrupted, error-free, or secure.
            </p>

            <h2 className="text-lg font-semibold mt-6 mb-3">10. Limitation of Liability</h2>
            <p className="mb-4">
              To the maximum extent permitted by law, Dauphin Island Food Mart shall not be liable for 
              any indirect, incidental, special, or consequential damages arising from your use of our 
              services or any products ordered through our website.
            </p>

            <h2 className="text-lg font-semibold mt-6 mb-3">11. Dispute Resolution</h2>
            <p className="mb-4">
              Any disputes arising from these terms or your use of our services shall be resolved through 
              good-faith negotiation. If a resolution cannot be reached, disputes shall be settled in 
              accordance with the laws of the State of Alabama.
            </p>

            <h2 className="text-lg font-semibold mt-6 mb-3">12. Changes to Terms</h2>
            <p className="mb-4">
              We may update these Terms of Service from time to time. Continued use of our services 
              after changes are posted constitutes acceptance of the new terms.
            </p>

            <h2 className="text-lg font-semibold mt-6 mb-3">13. Contact Information</h2>
            <p className="mb-4">For questions about these terms, please contact us:</p>
            <div className="bg-gray-50 p-4 rounded-xl">
              <p className="font-semibold">Dauphin Island Food Mart</p>
              <p>1000 Bienville Blvd</p>
              <p>Dauphin Island, AL 36528</p>
              <p className="mt-2">Phone: (251) 861-2320</p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
